import { useMemo } from 'react';
import type { CreationArtifactV1 } from '@djgg/creations';
import { useSubscription } from '../hooks/useSubscription.js';
import { useServices } from '../services/context.js';

/**
 * One read-only feed for a durable Create run. The atomic receipt is retained
 * until the subscription contains the same rows, so a slow realtime seed can
 * never make the first artifact disappear. Mounting this hook performs reads
 * only; provider dispatch belongs entirely to the server.
 */
export function useCreationArtifacts(
  runId: string | null,
  initialArtifacts: readonly CreationArtifactV1[] = [],
): CreationArtifactV1[] {
  const services = useServices();
  const rows = useSubscription<CreationArtifactV1[]>(
    cb => {
      if (!runId) {
        cb([]);
        return () => {};
      }
      return services.subscribe.creationArtifacts(runId, cb);
    },
    [],
    [runId],
  );
  return useMemo(() => {
    const merged = new Map<string, CreationArtifactV1>();
    for (const artifact of initialArtifacts) if (artifact.runId === runId) merged.set(artifact.artifactId, artifact);
    for (const artifact of rows) merged.set(artifact.artifactId, artifact);
    return [...merged.values()].sort((left, right) => left.arrivalSeq - right.arrivalSeq || left.artifactId.localeCompare(right.artifactId));
  }, [runId, initialArtifacts, rows]);
}
